/**
 * Guard shared by the "Move to Rehab" (pipeline) and "Start Rehab" (rehab page)
 * actions: a property that is actively listed for rent must be removed from
 * Rentals before it can enter rehab.
 *
 * "Actively listed" = a `rental_listings` row with status `active` or `rented`.
 * Draft / paused / archived listings do not block rehab.
 */
import { supabase } from "@/integrations/supabase/client";

/** Listing statuses that block a property from entering rehab. */
export const REHAB_BLOCKING_LISTING_STATUSES = ["active", "rented"] as const;

export const RENTAL_LISTING_BLOCK_MESSAGE =
  "This property is currently listed for rent. Remove it from Rentals before starting rehab.";

/** Thrown by mutations when an actively-listed property is sent to rehab. */
export class RentalListingBlockedError extends Error {
  constructor(message: string = RENTAL_LISTING_BLOCK_MESSAGE) {
    super(message);
    this.name = "RentalListingBlockedError";
  }
}

/**
 * True when the property has a rental listing in an active/rented state — i.e.
 * it can't be moved into rehab until it's removed from Rentals.
 */
export async function hasActiveRentalListing(
  pipelinePropertyId: string,
  workspaceId: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from("rental_listings")
    .select("id")
    .eq("workspace_id", workspaceId)
    .eq("pipeline_property_id", pipelinePropertyId)
    .in("status", REHAB_BLOCKING_LISTING_STATUSES as unknown as string[])
    .limit(1);
  if (error) throw error;
  return (data ?? []).length > 0;
}
